//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse} from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import BusinessEntity from './businessEntity.model';

/**
 * @service BusinessEntityService
 * @description Service for managing business entity data operations.
 * Provides HTTP methods for retrieving, creating, and managing business entities
 * within the trading community. Handles business-specific logic including
 * provider-farmer relationships, location validation, and business type filtering.
 * Extends base Service class with generic type BusinessEntity.
 *
 * @feature 1. Fetch all business entities with pagination
 * @feature 2. Retrieve farmers associated with specific providers
 * @feature 3. Validate pincodes using external API
 * @feature 4. Manage business code suggestions and availability
 * @feature 5. Cancel ongoing HTTP requests to prevent memory leaks
 *
 * @architecture
 * Extends base Service<BusinessEntity> providing REST API integration.
 * Implements specialized methods for different business roles (Employee, Provider, etc.).
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /tradingcommunity/businessentity/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class BusinessEntityService extends Service<BusinessEntity> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient
   *
   * Initializes service by calling parent Service constructor with baseUrl
   * configuration and service endpoint path.
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/tradingcommunity/businessentity/');
  }

  /**
   * @method getAllBusiness
   * @async
   * @description Fetch all business entities from the backend with large page size
   *
   * @returns {Promise<Array<BusinessEntity>>} Promise resolving to array of BusinessEntity objects
   * @throws Rejects promise with HttpErrorResponse if request fails
   * @side-effect Creates HTTP GET request and stores subscription in ongoingRequests
   */
  public getAllBusiness() {
    return new Promise<Array<BusinessEntity>>((resolve, reject) => {
      const request = this.httpClient.get<Array<BusinessEntity>>(`${this.baseUrl}${this.serviceUrl}?pageSize=50000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getAllfarmerByProviderID
   * @async
   * @description Fetch all farmers associated with a specific provider business
   *
   * Logic depends on logged-in user role (localStorage.loginPersonType):
   * - If 'EMPLOYEE': Fetches farmers for specific provider ID
   * - Otherwise: Fetches farmers for current context provider
   *
   * @param {string} businessId - Provider business ID to filter farmers
   * @returns {Promise<Array<BusinessEntity>>} Promise resolving to array of farmer BusinessEntity objects
   * @side-effect Reads localStorage.loginPersonType to determine query parameter
   */
  public getAllfarmerByProviderID(businessId: string) {
    let queryParam = '';
    if (localStorage.loginPersonType == 'EMPLOYEE') {
      queryParam = `feedProviderID/${businessId}`;
    } else {
      queryParam = `feedProviderID/`;
    }
    return new Promise<Array<BusinessEntity>>((resolve, reject) => {
      const request = this.httpClient.get<Array<BusinessEntity>>(`${this.baseUrl}${this.serviceUrl}${queryParam}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getBusinessCodeSuggestions
   * @async
   * @description Get suggestions for business codes based on name and input code
   *
   * @param {string} businessName - Name of the business
   * @param {string} businessCode - Partial or proposed business code
   * @returns {Promise<any>} Promise resolving to suggestion response
   */
  public getBusinessCodeSuggestions(businessName: string, businessCode: string) {
    return new Promise((resolve, reject) => {
      const request = this.httpClient.get(`${this.baseUrl}${this.serviceUrl}suggestions/${businessName}/${businessCode}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getCurrentDateFarmProviders
   * @async
   * @description Fetch farm providers active on the current date
   * @returns {Promise<Array<BusinessEntity>>} Promise resolving to array of BusinessEntity objects
   */
  public getCurrentDateFarmProviders() {
    return new Promise<Array<BusinessEntity>>((resolve, reject) => {
      const request = this.httpClient.get<Array<BusinessEntity>>(`${this.baseUrl}${this.serviceUrl}farmProviders/currentDate`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getCurrentDateFarmProvidersByproviderlogin
   * @async
   * @description Fetch current date farm providers for logged-in provider context
   * @returns {Promise<Array<BusinessEntity>>} Promise resolving to array of BusinessEntity objects
   */
  public getCurrentDateFarmProvidersByproviderlogin() {
    return new Promise<Array<BusinessEntity>>((resolve, reject) => {
      const request = this.httpClient.get<Array<BusinessEntity>>(`${this.baseUrl}${this.serviceUrl}currentDate/farmProviderId/`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getAllFarmCounts
   * @async
   * @description Get summary counts of farms for feed provider context
   * @returns {Promise<Array<BusinessEntity>>} Promise resolving to array of BusinessEntity objects with counts
   */
  public getAllFarmCounts() {
    return new Promise<Array<BusinessEntity>>((resolve, reject) => {
      const request = this.httpClient.get<Array<BusinessEntity>>(`${this.baseUrl}${this.serviceUrl}summary/feedProviderID//technicianID/-1`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getAllBusinessEntity
   * @async
   * @description Fetch all business entities with 'farmBuddy' person association
   * @returns {Promise<Array<BusinessEntity>>} Promise resolving to array of BusinessEntity objects
   */
  public getAllBusinessEntity() {
    return new Promise<Array<BusinessEntity>>((resolve, reject) => {
      const request = this.httpClient.get<Array<BusinessEntity>>(`${this.baseUrl}${this.serviceUrl}persons/farmBuddy`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method checkPincode
   * @async
   * @description Validate postal code and retrieve location details via external API
   *
   * Makes HTTP GET request to worldpostallocations.com API
   *
   * @param {any} pincode - Postal code to validate
   * @param {any} countryCode - Country code context
   * @returns {Promise<BusinessEntity>} Promise resolving to location details mapped to BusinessEntity
   */
  public checkPincode(pincode, countryCode) {
    return new Promise<BusinessEntity>((resolve, reject) => {
      const request = this.httpClient.get<BusinessEntity>(`https://api.worldpostallocations.com/pincode?postalcode=${pincode}&countrycode=${countryCode}`).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getProviderBusiness
   * @async
   * @description Fetch provider businesses filtered by business type
   * @param {any} btype - Business type identifier
   * @returns {Promise<Array<BusinessEntity>>} Promise resolving to array of BusinessEntity objects
   */
  public getProviderBusiness(btype: any) {
    return new Promise<Array<BusinessEntity>>((resolve, reject) => {
      const request = this.httpClient.post<Array<BusinessEntity>>(`${this.baseUrl}${this.serviceUrl}businessType`, {
        type: btype
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getTechnicianLeads
   * @async
   * @description Get leads summary for a specific technician under a provider
   * @param {string} providerBusinessId - Provider business ID
   * @param {string} personId - Technician person ID
   */
  public getTechnicianLeads(providerBusinessId: string, personId: string) {
    return new Promise<Array<BusinessEntity>>((resolve, reject) => {
      const request = this.httpClient.get<Array<BusinessEntity>>(`${this.baseUrl}${this.serviceUrl}summary/feedProviderID/${providerBusinessId}/technicianID/${personId}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getProviderPersonsByBusinessID
   * @async
   * @description Fetch provider persons/production line details by hatchery/business ID
   * @param {string} businessID - Business ID (Hatchery ID)
   */
  public getProviderPersonsByBusinessID(businessID: string) {
    return new Promise<Array<BusinessEntity>>((resolve, reject) => {
      const request = this.httpClient.get<Array<BusinessEntity>>(`${this.baseUrl}${this.serviceUrl}productionLine?hatcheryId=${businessID}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks
   *
   * Iterates through ongoingRequests array and unsubscribes from each active
   * subscription. Clears the requests array after unsubscribing from all.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests
   * - Clears ongoingRequests array
   *
   * @usage Called during component ngOnDestroy to cleanup subscriptions
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
